// Color guide portrait photos hosted on Cloudflare R2.
// Keyed by color NAME (matches colorOptions / screenColorOptions values exactly,
// e.g. 'Sunflower', 'Smoked Bronze', 'Turquoise Teal', 'Stainless Steel').
//
// Each portrait can optionally specify bodyColor and screenColor. These drive
// the labels shown in the expanded photo view AND the "Apply These Colors"
// button. They must match a colorOptions value (for body) or a colorOptions /
// screenColorOptions value (for screen) exactly, otherwise the Apply lookup
// will fall back to the current color guide entry.
//
// IMPORTANT name aliases (photographer used Cerakote paint names, not the
// app's marketing names):
//   sunglow-yellow-*.avif → maps to 'Sunflower'      (Cerakote paint is Sunglow Yellow)
//   sunflower-*.avif      → maps to 'Formula Yellow' (Cerakote paint is Sunflower)

const BASE = 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/color-guide/mic-photos/'

// Encode spaces only (modern browsers and R2 handle bare parens fine).
const enc = (s) => BASE + s.replace(/ /g, '%20')

// Reuse-by-reference: Satin Aluminum and Stainless Steel share the same
// three photos and identical body/screen labels until dedicated Stainless
// Steel photos are uploaded.
const satinAluminumPortraits = [
  { imageUrl: enc('satin-aluminum-OHM02140 (15).avif'), bodyColor: 'Satin Aluminum', screenColor: 'Stainless Steel' },
  { imageUrl: enc('satin-aluminum-OHM02148 (23).avif'), bodyColor: 'Satin Aluminum', screenColor: 'Stainless Steel' },
  { imageUrl: enc('satin-aluminum-OHM02151 (26).avif'), bodyColor: 'Satin Aluminum', screenColor: 'Stainless Steel' }
]

export const colorGuidePortraits = {
  'Aqua Lucia': [{ imageUrl: enc('aqua-lucia-1.avif') }, { imageUrl: enc('aqua-lucia-2.avif') }, { imageUrl: enc('aqua-lucia-3.avif') }],
  Black: [{ imageUrl: enc('Black-OHM01968 (06).avif') }, { imageUrl: enc('Black-OHM01969 (07).avif') }, { imageUrl: enc('Black-OHM01972 (10).avif') }],
  Brass: [
    { imageUrl: enc('all-brass-OHM02222 (05)-Edit 2.avif') },
    { imageUrl: enc('comp angle all-brass-OHM02261 (44)-Edit.avif') },
    { imageUrl: enc('all-brass-OHM02266 (49).avif') }
  ],
  'Burnt Orange': [{ imageUrl: enc('burnt-orange-1.avif') }, { imageUrl: enc('burnt-orange-2.avif') }, { imageUrl: enc('burnt-orange-3.avif') }],
  'Calabrian Red': [
    { imageUrl: enc('calabrian-red-OHM01938 (03).avif') },
    { imageUrl: enc('calabrian-red-OHM01939 (05).avif') },
    { imageUrl: enc('calabrian-red-OHM01940 (07).avif') }
  ],
  'Coffee Cream': [
    { imageUrl: enc('coffee-cream-OHM02168 (02).avif') },
    { imageUrl: enc('coffee-cream-OHM02169 (04).avif') },
    { imageUrl: enc('coffee-cream-OHM02170 (06).avif') }
  ],
  Copper: [{ imageUrl: enc('copper-1.avif') }, { imageUrl: enc('OHM01959.avif') }, { imageUrl: enc('OHM01960.avif') }],
  'Dark Fuchsia': [
    { imageUrl: enc('dark-fuchsia-OHM01931 (06).avif') },
    { imageUrl: enc('dark-fuchsia-OHM01932 (04).avif') },
    { imageUrl: enc('dark-fuchsia-OHM01933 (02).avif') }
  ],
  Evergreen: [
    { imageUrl: enc('Evergreen-OHM01916 (08).avif') },
    { imageUrl: enc('Evergreen-OHM01917 (06).avif') },
    { imageUrl: enc('Evergreen-OHM01919 (02).avif') }
  ],
  // Cerakote "Sunflower (H-317)" paint = the color named 'Formula Yellow' in app.
  'Formula Yellow': [{ imageUrl: enc('sunflower-1.avif') }, { imageUrl: enc('sunflower-2.avif') }, { imageUrl: enc('sunflower-3.avif') }],
  Gold: [{ imageUrl: enc('gold-1.avif') }, { imageUrl: enc('gold-2.avif') }, { imageUrl: enc('gold-3.avif') }],
  'Meridian Blue': [
    { imageUrl: enc('Meridian-Blue-OHM01928 (02).avif') },
    { imageUrl: enc('Meridian-Blue-OHM01929 (04).avif') },
    { imageUrl: enc('Meridian-Blue-OHM01930 (06).avif') }
  ],
  'Mosslight Green': [
    { imageUrl: enc('Mosslight-Green-OHM01934 (01).avif') },
    { imageUrl: enc('Mosslight-Green-OHM01935 (03).avif') },
    { imageUrl: enc('Mosslight-Green-OHM01936 (05).avif') }
  ],
  'Petal Pink': [{ imageUrl: enc('petal-pink-1.avif') }, { imageUrl: enc('petal-pink-2.avif') }, { imageUrl: enc('petal-pink-3.avif') }],
  'Rose Gold': [
    { imageUrl: enc('Rose-Gold-OHM01900 (06).avif') },
    { imageUrl: enc('Rose-Gold-OHM01901 (04).avif') },
    { imageUrl: enc('Rose-Gold-OHM01902 (02).avif') }
  ],
  'Satin Aluminum': satinAluminumPortraits,
  'Stainless Steel': satinAluminumPortraits,
  'Smoked Bronze': [{ imageUrl: enc('smoked-bronze-1.avif') }, { imageUrl: enc('smoked-bronze-2.avif') }, { imageUrl: enc('smoked-bronze-3.avif') }],
  'Spiced Berry': [{ imageUrl: enc('spiced-berry-2.avif') }, { imageUrl: enc('spiced-berry-3.avif') }, { imageUrl: enc('spiced-berry-4.avif') }],
  // Cerakote "Sunglow Yellow (H-144)" paint = the color named 'Sunflower' in app.
  Sunflower: [{ imageUrl: enc('sunglow-yellow-1.avif') }, { imageUrl: enc('sunglow-yellow-2.avif') }, { imageUrl: enc('sunglow-yellow-3.avif') }],
  Terracotta: [{ imageUrl: enc('terracotta-1.avif') }, { imageUrl: enc('terracotta-2.avif') }, { imageUrl: enc('terracotta-3.avif') }],
  'Turquoise Teal': [{ imageUrl: enc('teal-OHM02200 (02).avif') }, { imageUrl: enc('teal-OHM02201 (04).avif') }, { imageUrl: enc('teal-OHM02202 (01).avif') }],
  'Vera Violet': [
    { imageUrl: enc('Vera-Violet-OHM01912 (03).avif') },
    { imageUrl: enc('Vera-Violet-OHM01913 (05).avif') },
    { imageUrl: enc('Vera-Violet-OHM01914 (07).avif') }
  ],
  'Verdant Green': [
    { imageUrl: enc('Verdant-Green-OHM01921 (06).avif') },
    { imageUrl: enc('Verdant-Green-OHM01922 (04).avif') },
    { imageUrl: enc('Verdant-Green-OHM01923 (02).avif') }
  ],
  White: [{ imageUrl: enc('white-1.avif') }, { imageUrl: enc('white-2.avif') }, { imageUrl: enc('white-3.avif') }]
}
